/* eslint-disable global-require */
import { create as createHash } from 'inthash';
import communities from './communities';
import defaultPage from './default';
import empty from './empty';
import home from './home';
import notFound from './not-found';
import ranking from './ranking';
import events from './events';
import sponsors from './sponsors';
import microsite from './microsite';
import event from './event';
import challenge from './challenge';
import mapPage from './map';
import { getBaseUrl } from '../utils/utility';
import { HTTP_STATUS_CODE_BAD_REQUEST, HTTP_STATUS_CODE_NOT_FOUND } from '../api/http-error';
import { getPageContent } from './helper';

const TYPE_MAP = {
  communities_page: communities,
  default_page: defaultPage,
  empty_page: empty,
  home_page: home,
  ranking_page: ranking,
  sponsors_page: sponsors,
  challenge_page: challenge,
  events,
};

// These constant numbers are auto-generated using inthashgen.
// see https://github.com/wan2land/inthash
const { decode } = createHash(760629563, 717858803, 1727823178);

const getMicrosite = async (context, params) => {
  try {
    const { name } = params;
    const { store, fetch, gda, prismicApi } = context;

    // Extract id from name
    let id = name.split('-');
    id = id[id.length - 1];
    if (!id) {
      return null;
    }

    const url = `/api/microsites/${id}`;
    const content = await getPageContent(url, store, fetch, () => gda.getMicrositePage(id, prismicApi));

    if (content) {
      context.content = content;
      return await microsite(context, params);
    }
  } catch (err) {
    if (err.statusCode === HTTP_STATUS_CODE_NOT_FOUND || err.statusCode === HTTP_STATUS_CODE_BAD_REQUEST) {
      // Redirect to site not found
      return context.next();
    }

    throw err;
  }

  // If route wasn't found ignore
  return null;
};

const getChallenge = async (context, params) => {
  try {
    const { id } = params;
    const { store, fetch, gda, prismicApi } = context;

    const resolvedId = decode(id);

    const url = `/api/challenges/${resolvedId}`;
    const content = await getPageContent(url, store, fetch, () => gda.getChallengePage(resolvedId, prismicApi));

    if (content) {
      context.content = content;
      return await challenge(context, params);
    }
  } catch (err) {
    if (err.statusCode === HTTP_STATUS_CODE_NOT_FOUND || err.statusCode === HTTP_STATUS_CODE_BAD_REQUEST) {
      return context.next();
    }

    throw err;
  }

  return null;
};

const getMicrositeEvent = async (context, params) => {
  try {
    const { name, eventId } = params;
    const { store, fetch, gda, prismicApi } = context;

    // Extract id from name
    let id = name.split('-');
    id = id[id.length - 1];
    if (!id) {
      return null;
    }

    const url = `/api/microsites/${id}/event/${eventId}`;
    let content;

    if (process.env.BROWSER) {
      content = await fetch(url)
        .then(response => response.json());
    } else {
      content = await gda.getMicrositeEventPage(id, eventId, prismicApi);
    }

    if (content) {
      context.content = content;
      return await event(context, params);
    }
  } catch (err) {
    if (err.statusCode === HTTP_STATUS_CODE_NOT_FOUND || err.statusCode === HTTP_STATUS_CODE_BAD_REQUEST) {
      // Redirect to site not found
      return context.next();
    }

    throw err;
  }

  // If route wasn't found ignore
  return null;
};

// The top-level (parent) route
const routes = {
  path: '',

  // Keep in mind, routes are evaluated in order
  children: [
    {
      path: '/challenge/:id',
      action: getChallenge,
    },
    {
      path: '/gemeinden/:name',
      action: getMicrosite,
    },
    {
      path: '/gemeinden/:name/event/:eventId',
      action: getMicrositeEvent,
    },
    {
      path: '/communes/:name',
      action: getMicrosite,
    },
    {
      path: '/communes/:name/activite/:eventId',
      action: getMicrositeEvent,
    },
    {
      path: '/comuni/:name',
      action: getMicrosite,
    },
    {
      path: '/comuni/:name/evento/:eventId',
      action: getMicrositeEvent,
    },
    {
      path: '/:level1?/:level2?/:level3?/:level4?',
      action: async (context, params) => {
        try {
          const { path, store, locale, fetch, prismicApi, gda } = context;
          const dynamicRoutes = store.get('routes');

          // Find the first matching route
          const route = dynamicRoutes[locale].find(child => child.path === path);

          if (route) {
            const mappedPage = TYPE_MAP[route.type];

            // Augment context with route information
            context.route = route;

            if (mappedPage) {
              const { id } = route;
              const url = `/api/page/${id}`;

              const content = await getPageContent(url, store, fetch, () => prismicApi.getPage(id, gda));

              if (content) {
                context.content = content;
                return await mappedPage(context, params);
              }
            }
          }
        } catch (err) {
          context.next(err);
        }

        // If route wasn't found ignore
        return null;
      },
    },
    {
      path: '/iframe-map',
      action: mapPage,
    },
    {
      path: '(.*)',
      action: notFound,
    },
  ],

  async action({ hostname, i18n, next, protocol }) {
    // Execute each child route until one of them return the result
    const route = await next();

    if (route) {
      const baseUrl = getBaseUrl(protocol, hostname);

      const defaultOgImage = `${baseUrl}${i18n.t('meta.og.image')}`;

      // Provide default values for title, description etc.
      route.title = route.title || i18n.t('meta.title');
      route.description = route.description || i18n.t('meta.description');
      route.ogImage = route.ogImage && route.ogImage.url ? route.ogImage : { url: defaultOgImage };
      route.alternateLocales = route.alternateLocales || {};

      return route;
    }

    throw new Error('Error occurred while building content of route');
  },
};

// The error page is available by permanent url for development mode
if (__DEV__) {
  routes.children.unshift({
    path: '/error',
    action: require('./error').default,
  });
}

export default routes;
